/* eslint-disable max-lines */
import {
  Component, ViewEncapsulation, ChangeDetectionStrategy, ChangeDetectorRef, Input
} from '@angular/core';
import { Observable, combineLatest, zip } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';

import { PackagesResultFiltersFacade } from '@packages/packages-result/store/package-result-filters.facade';
import { isEmpty } from '@app/util/util';
import { AppFacade } from '@app/app.facade';
import { getFiltersByBarWithCriteria } from '@shared/magic-filter-bar/hotel-filter-bar/mappers/hotel-filters-in-bar.mapper';
import {
  HotelFilterApplier, HotelFilterApplierValue, HotelFilterProvider, HotelFilterTypes
} from '@shared/hotel-itinerary/hotel-filters';
import { CheckboxFilter, PickedRange } from '@shared/filters/entities/fetch-filters.response';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import {
  detailPackageOulet, singleAirFilterRoute, singleHotelFilterRoute
} from '@packages/packages-result/constants/packages-result.routes';
import {
  HotelFilterBarBaseContainer
} from '@app/modules/shared/magic-filter-bar/hotel-filter-bar/abstracts/hotel-filter-bar-base.container';
import { PackageFilterProvider } from '@packages/packages-result/constants/package-filter-provider';
import { SharedSelectedFilterAirValue } from '@shared/magic-filter-bar/entities/shared-props';
import { AirportsFilter } from '@flight-results/entities/request-search.payload';
import { PackageSeachPayload } from '@packages/packages-result/entities/packages-search';
import { getAirFiltersByBarWithCriteria } from '@shared/magic-filter-bar/air-filter-bar/mappers/air-filters-in-bar.mapper';
import { SorterTypes } from '@shared/filters/entities/sorter-options';
import { FilterApplier, FilterTypes } from '@shared/flight-itinerary/entities/filters';
import { PackagesResultFacade } from '@packages/packages-result/store/packages-result.facade';
import { AirFilterVm, HotelFilterVm } from '@shared/magic-filter-bar/entities/filter-types';

@Component({
  selector: 'app-package-filter-bar',
  templateUrl: './package-filter-bar.container.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PackageFilterBarContainer extends HotelFilterBarBaseContainer {

  @Input() provider = PackageFilterProvider.HOTEL;
  @Input() params = {} as PackageSeachPayload;

  public pickedRanges = {} as PickedRange;
  public pickedAirportCheckboxes = {} as AirportsFilter;

  constructor(
    protected route: ActivatedRoute,
    protected appFacade: AppFacade,
    protected cdr: ChangeDetectorRef,
    private readonly packagesResultFiltersFacade: PackagesResultFiltersFacade,
    private readonly packagesResultsFacade: PackagesResultFacade,
    private readonly domManipulateService: DomManipulateService,
    private readonly router: Router,
  ) {
    super(route, appFacade, cdr);
  }

  get barHotelFilters$(): Observable<HotelFilterVm[]> {
    return this.hotelFilters$.pipe(switchMap(filters =>
      zip(this.tagTypeIsPrime$, this.tagTypeIsRappicard$).pipe(
        map(([isPrime, hasRappiCard]) => {
          return getFiltersByBarWithCriteria(filters, isPrime, hasRappiCard);
        })
      )));
  }

  get barAirFilters$(): Observable<AirFilterVm[]> {
    return this.airFilterVm$.pipe(map(filters => getAirFiltersByBarWithCriteria(filters)));
  }

  get hasFilters$(): Observable<boolean> {
    return combineLatest([this.hotelFilters$, this.airFilterVm$]).pipe(map(([hotelFilters, airFilterVm]) => {
      return this.provider === PackageFilterProvider.HOTEL ? !isEmpty(hotelFilters) : !isEmpty(airFilterVm);
    }));
  }

  get airOrderCriteria$(): Observable<SorterTypes> {
    return this.packagesResultFiltersFacade.airOrderCriteria$;
  }

  get airOrderDesc$(): Observable<boolean> {
    return this.packagesResultFiltersFacade.airOrderDesc$;
  }

  get airOrderCriteriaSectorNumber$(): Observable<number> {
    return this.packagesResultFiltersFacade.airOrderCriteriaSectorNumber$;
  }

  get airFilterVm$(): Observable<AirFilterVm[]> {
    return this.packagesResultFiltersFacade.packageAirFilters$;
  }

  get sortFilterHasBeenChangedByPackage$(): Observable<boolean> {
    return this.packagesResultFiltersFacade.sortFilterHasBeenChanged$;
  }

  get packageFilterProvider(): typeof PackageFilterProvider {
    return PackageFilterProvider;
  }

  get codePlacesByPackageSearch(): { departure: string, arrival: string } {
    return { departure: this.params?.DeparturePoint, arrival: this.params?.ArrivalPoint };
  }

  get isHotelView(): boolean {
    return this.provider === PackageFilterProvider.HOTEL;
  }

  private get hotelFilters$(): Observable<HotelFilterVm[]> {
    return this.packagesResultFiltersFacade.packageHotelFilters$;
  }

  public sharedShouldShowFiltersByPackage(value: boolean): void {
    this.sortFilterHasBeenChangedByPackage$.pipe(
      take(1),
      tap((sortFilterHasBeenChangedByPackage) => {
        this.sharedShouldShowFilters(value || sortFilterHasBeenChangedByPackage);
      })
    ).subscribe().unsubscribe();
  }

  public exclusiveOfferFilterAction(): void {
    const payload = this.pickedMemberRatesOnly ?
      { type: HotelFilterTypes.MemberRatesOnly, value: false } :
      { type: HotelFilterTypes.MemberRatesOnly, value: true };
    this.applyHotelFilterVmByPackage(payload);
  }

  public checkboxHotelAction(event: {
    filter: HotelFilterVm, isMultiple: boolean, checked: boolean, isCheckbox: boolean
  }): void {
    if (event.isMultiple) {
      this.toggleIndividualHotelFilter(event.filter, []);
    } else if (event.checked) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.removeHotelCheckboxFilter((event.filter.value as any[])[0], event.filter.type, event.isCheckbox);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.applyHotelFilterVmByPackage({ type: event.filter.type, value: (event.filter.value as any[])[0].Text });
    }
  }

  public radioHotelAction(event: { filter: HotelFilterVm, isMultiple: boolean }): void {
    if (event.isMultiple) {
      this.toggleIndividualHotelFilter(event.filter, null);
    }
  }

  public removeAirCheckboxFilter(item: CheckboxFilter, type: string): void {
    const payload = { type: type as FilterTypes, value: item.Text, removable: true };
    this.applyAirFilterVmByPackage(payload);
  }

  public removeHotelCheckboxFilter(item: CheckboxFilter, type: string, isCheckboxFilter: boolean): void {
    const payload = isCheckboxFilter ? { type: type as HotelFilterTypes, value: item.Text, removable: true } : {
      type: type as HotelFilterTypes, value: this.selectedStarItems.filter(_item => _item.Text !== item.Text)
    };
    this.applyHotelFilterVmByPackage(payload);
  }

  public toggleIndividualHotelFilter(filter: HotelFilterVm, resetValue: HotelFilterApplierValue): void {
    if (filter) {
      this.router.navigate([{ outlets: { [detailPackageOulet]: [singleHotelFilterRoute] } }], {
        state: {
          filter: filter, isHotelByPackage: true,
          resetValue: resetValue, viewType: this.viewType,
        }, relativeTo: this.route, skipLocationChange: true
      });
    }
  }

  public sortAirAction(event: { filter: AirFilterVm, isMultiple: boolean }): void {
    this.toggleIndividualAirFilter(event.filter);
  }

  public checkboxAirAction(event: { filter: AirFilterVm, isMultiple: boolean, checked: boolean }): void {
    if (event.isMultiple) {
      this.toggleIndividualAirFilter(event.filter);
    } else if (event.checked) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.removeAirCheckboxFilter((event.filter.value as any[])[0], event.filter.type);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.applyAirFilterVmByPackage({ type: event.filter.type, value: (event.filter.value as any[])[0].Text });
    }
  }

  public rangeAirAction(event: { filter: AirFilterVm, isMultiple: boolean }): void {
    if (event.isMultiple) {
      this.toggleIndividualAirFilter(event.filter);
    }
  }

  public toggleIndividualAirFilter(filter: AirFilterVm): void {
    if (filter) {
      this.router.navigate([{ outlets: { [detailPackageOulet]: [singleAirFilterRoute] } }], {
        state: {
          filter: filter, isAirByPackage: true, isMobile: true,
        }, relativeTo: this.route, skipLocationChange: true
      });
    }
  }

  public airlineFilterApply(event: { filter: FilterApplier[] }): void {
    this.packagesResultsFacade.fetchAirWithMultipleFilter(event.filter, true);
  }

  public sharedSelectedAirFilters(filters: { [key: string]: SharedSelectedFilterAirValue }): void {
    this.pickedAirportCheckboxes = filters.pickedAirportCheckboxes as AirportsFilter;
    this.pickedRanges = filters.pickedRanges as PickedRange;
    this.pickedCheckboxes = filters.pickedCheckboxes as string[];
    this.cdr.detectChanges();
  }

  private applyHotelFilterVmByPackage(filter: HotelFilterApplier): void {
    if (this.viewType === HotelFilterProvider.HotelResults) {
      this.domManipulateService.resetScrollPosition();
      this.packagesResultFiltersFacade.fetchHotelsWithFilters(filter, true);
    } else {
      this.packagesResultFiltersFacade.fetchHotelsMarkersWithFilters(filter);
    }
  }

  private applyAirFilterVmByPackage(filter: FilterApplier): void {
    this.packagesResultsFacade.fetchAirWithFilter(filter, true);
  }

}
